import React, { useCallback, useState } from 'react';
import TableRow from '@mui/material/TableRow';
import TableCell from 'src/components/Table/TableCell';
import PropTypes from 'prop-types';
import selectFormatDate from 'src/utils/selectDateFormat';
import dateFormatType from 'src/utils/dateFormatType';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MoreMenu from 'src/components/MoreMenu/MoreMenu';
import { Tooltip, Chip } from '@mui/material';
import DocumentViewerDialog from 'src/components/DocumentViewer/DocumentViewerDialog';
import { SantanderClabeActions } from 'src/utils/constants/clients';
import DescriptionIcon from '@mui/icons-material/Description';
import StatusIcon from 'src/components/StatusIcon';
import IconStatusAndColor from 'src/utils/constants/common';
import ReconciliationStatus from './reconciliationStatus.enum';

const SantanderReportsRow = ({
  id,
  date,
  transactionCount,
  totalAmount,
  reconciliationStatus,
  unreconciledPayments,
  rawReport,
  excelReport
}) => {
  const { t } = useTranslation();
  const [openOption, setOpenOption] = useState('');

  const handlePreviewPdf = () => {
    setOpenOption(SantanderClabeActions.RAW_SANTANDER_PAYMENT_REPORT);
  };

  const handlePreviewExcel = () => {
    setOpenOption(SantanderClabeActions.EXCEL_SANTANDER_PAYMENT_REPORT);
  };

  const handleCloseDialog = () => {
    setOpenOption('');
  };

  const getReconciliationStatusIcon = () => {
    if (reconciliationStatus === ReconciliationStatus.RECONCILED) {
      return <StatusIcon type={IconStatusAndColor.OK.status} />;
    }
    return <StatusIcon type={IconStatusAndColor.WARNING.status} />;
  };

  const getReconciliationStatusTooltip = () => {
    if (unreconciledPayments === 0) {
      return t('SANTANDER_REPORTS.RECONCILIATION_COMPLETED');
    }
    return t('SANTANDER_REPORTS.RECONCILIATION_PENDING', {
      count: unreconciledPayments
    });
  };

  const handleClickMore = action => {
    switch (action) {
      case SantanderClabeActions.RAW_SANTANDER_PAYMENT_REPORT:
        handlePreviewPdf();
        break;
      case SantanderClabeActions.EXCEL_SANTANDER_PAYMENT_REPORT:
        handlePreviewExcel();
        break;
      default:
        break;
    }
  };

  const getMoreMenuOptions = useCallback(() => {
    const options = [];
    if (rawReport) {
      options.push({
        name: SantanderClabeActions.RAW_SANTANDER_PAYMENT_REPORT,
        label: 'COMMON.RAW_SANTANDER_PAYMENT_REPORT',
        icon: <DescriptionIcon />
      });
    }

    if (excelReport) {
      options.push({
        name: SantanderClabeActions.EXCEL_SANTANDER_PAYMENT_REPORT,
        label: 'COMMON.EXCEL_SANTANDER_PAYMENT_REPORT',
        icon: <DescriptionIcon />
      });
    }

    return options;
  }, [rawReport, excelReport]);
  return (
    <>
      <TableRow hover align="center">
        <TableCell align="center">
          <Link
            to={{
              pathname: `/santander-reports/referenced-payments/detail/${id}`,
              state: { reportDate: date }
            }}
          >
            {selectFormatDate(date, dateFormatType.DAY)}
          </Link>
        </TableCell>
        <TableCell align="center">{transactionCount}</TableCell>
        <TableCell align="center">
          {formatAmountWithDecimals(totalAmount)}
        </TableCell>
        <TableCell align="center">
          <Tooltip title={getReconciliationStatusTooltip()}>
            <Chip
              icon={getReconciliationStatusIcon()}
              label={
                reconciliationStatus === ReconciliationStatus.RECONCILED
                  ? t('COMMON.COMPLETED')
                  : t('COMMON.PENDING')
              }
              size="small"
              sx={{ backgroundColor: 'transparent' }}
            />
          </Tooltip>
        </TableCell>
        <TableCell align="right">
          <MoreMenu
            deleteDisabled
            editDisabled
            key={id}
            options={getMoreMenuOptions()}
            handleClickOption={handleClickMore}
          />
        </TableCell>
      </TableRow>
      {rawReport && (
        <DocumentViewerDialog
          open={
            openOption === SantanderClabeActions.RAW_SANTANDER_PAYMENT_REPORT
          }
          onClose={handleCloseDialog}
          documentUrl={rawReport.documentUrl}
          fileName={`santander-report-${id}-${date}.txt`}
        />
      )}
      {excelReport && (
        <DocumentViewerDialog
          open={
            openOption === SantanderClabeActions.EXCEL_SANTANDER_PAYMENT_REPORT
          }
          onClose={handleCloseDialog}
          documentUrl={excelReport.documentUrl}
          fileName={`santander-report-${id}-${date}.xlsx`}
        />
      )}
    </>
  );
};

SantanderReportsRow.propTypes = {
  id: PropTypes.number.isRequired,
  date: PropTypes.string.isRequired,
  transactionCount: PropTypes.number.isRequired,
  totalAmount: PropTypes.number.isRequired,
  reconciliationStatus: PropTypes.string.isRequired,
  unreconciledPayments: PropTypes.number.isRequired,
  rawReport: PropTypes.object.isRequired,
  excelReport: PropTypes.object.isRequired
};

export default SantanderReportsRow;
